(() => {
  "use strict";

  const moneyFormat = (cents) => "₹" + (cents / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  /* ---------- gallery ----------
     The main image area is a horizontal scroll-snap track with one slide per
     product image (.pdp-gallery-track), so touch swipe is native scrolling.
     goToImage() is the single entry point for thumbnails, the desktop
     arrows and variant changes; the scroll listener keeps the thumbnail,
     "n / N" tag and arrow state in sync when the user swipes instead. */
  const galleryTrack = document.getElementById("pdpGalleryTrack");
  const gallerySlides = galleryTrack ? Array.from(galleryTrack.children) : [];
  const galleryBrief = document.getElementById("pdpGalleryBrief");
  const galleryTag = document.getElementById("pdpGalleryTag");
  const galleryPrev = document.getElementById("pdpGalleryPrev");
  const galleryNext = document.getElementById("pdpGalleryNext");
  const thumbs = Array.from(document.querySelectorAll(".pdp-thumb"));
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  let galleryIndex = 0;
  // Index a programmatic (smooth) scroll is heading to. While set, the scroll
  // listener ignores the intermediate slides it passes so the active thumb
  // doesn't flicker through them.
  let galleryNavTarget = null;
  let galleryNavTimer = null;

  function clearGalleryNavTarget() {
    galleryNavTarget = null;
    clearTimeout(galleryNavTimer);
  }

  // Update the non-scroll UI (thumb highlight, tag, brief, arrow state).
  function syncGalleryUI(index) {
    galleryIndex = index;
    const thumb = thumbs[index];
    if (galleryBrief && thumb) galleryBrief.textContent = thumb.dataset.brief || "";
    if (galleryTag && thumb) galleryTag.textContent = thumb.dataset.tag || "";
    thumbs.forEach((t, i) => t.classList.toggle("active", i === index));
    if (galleryPrev) galleryPrev.disabled = index <= 0;
    if (galleryNext) galleryNext.disabled = index >= gallerySlides.length - 1;
  }

  function goToImage(index) {
    if (!galleryTrack || !gallerySlides.length) return;
    const target = Math.max(0, Math.min(gallerySlides.length - 1, index));
    galleryNavTarget = target;
    clearTimeout(galleryNavTimer);
    // Safety net: never leave the scroll listener muted if the scroll is
    // cut short (already at target, interrupted, etc.).
    galleryNavTimer = setTimeout(clearGalleryNavTarget, 800);
    syncGalleryUI(target);
    galleryTrack.scrollTo({
      left: target * galleryTrack.clientWidth,
      behavior: reduceMotion.matches ? "auto" : "smooth",
    });
  }

  if (galleryTrack) {
    let scrollTicking = false;
    galleryTrack.addEventListener(
      "scroll",
      () => {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(() => {
          scrollTicking = false;
          const index = Math.max(
            0,
            Math.min(gallerySlides.length - 1, Math.round(galleryTrack.scrollLeft / galleryTrack.clientWidth))
          );
          if (galleryNavTarget !== null) {
            if (index === galleryNavTarget) clearGalleryNavTarget();
            return;
          }
          if (index !== galleryIndex) syncGalleryUI(index);
        });
      },
      { passive: true }
    );

    // A touch/drag by the user takes over from any in-flight programmatic scroll.
    galleryTrack.addEventListener("pointerdown", clearGalleryNavTarget, { passive: true });

    // Keep the current slide aligned when the track's width changes
    // (rotation, window resize, crossing the mobile/desktop breakpoint).
    if ("ResizeObserver" in window) {
      new ResizeObserver(() => {
        galleryTrack.scrollTo({ left: galleryIndex * galleryTrack.clientWidth, behavior: "auto" });
      }).observe(galleryTrack);
    }
  }

  thumbs.forEach((thumb) => {
    thumb.addEventListener("click", () => goToImage(Number(thumb.dataset.index)));
  });
  if (galleryPrev) galleryPrev.addEventListener("click", () => goToImage(galleryIndex - 1));
  if (galleryNext) galleryNext.addEventListener("click", () => goToImage(galleryIndex + 1));

  // Show the slide for a variant's featured image (matched by image id).
  function showVariantImage(featuredImage) {
    if (!featuredImage) return;
    const index = gallerySlides.findIndex((s) => s.dataset.imageId === String(featuredImage.id));
    if (index > -1) goToImage(index);
  }

  /* ---------- variant selection ---------- */
  const variantWrapper = document.getElementById("pdpVariants");
  const productDataEl = document.getElementById("pdpProductJSON");
  const variantIdInput = document.getElementById("pdpVariantId");
  const priceEl = document.getElementById("pdpPrice");
  const stockEl = document.getElementById("pdpStock");
  const floatPriceEl = document.getElementById("pdpFloatPrice");
  const floatVariantEl = document.getElementById("pdpFloatVariant");
  const addBtn = document.getElementById("pdpAddBtn");
  const floatAdd = document.getElementById("pdpFloatAdd");
  const initialVariantAvailable = addBtn ? !addBtn.disabled : false;

  let productData = null;
  if (productDataEl) {
    try {
      productData = JSON.parse(productDataEl.textContent);
    } catch (e) {
      productData = null;
    }
  }

  const selectedOptions = productData
    ? (productData.variants.find((v) => v.id === Number(variantIdInput.value))?.options || []).slice()
    : [];

  function findMatchingVariant() {
    if (!productData) return null;
    return productData.variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function applyVariant(variant) {
    if (!variant) {
      if (variantIdInput) variantIdInput.value = "";
      if (floatPriceEl) floatPriceEl.textContent = "Select options";
      if (floatVariantEl) floatVariantEl.textContent = selectedOptions.filter(Boolean).join(" / ");
      if (stockEl) stockEl.textContent = "Unavailable";
      [addBtn, floatAdd].forEach((btn) => {
        if (btn) {
          btn.disabled = true;
          btn.textContent = "Unavailable";
        }
      });
      return;
    }

    if (variantIdInput) variantIdInput.value = variant.id;
    if (floatVariantEl) floatVariantEl.textContent = variant.title === "Default Title" ? "" : variant.title;

    const available = variant.available;
    const priceText = moneyFormat(variant.price);

    if (priceEl) priceEl.textContent = priceText;
    if (floatPriceEl) floatPriceEl.textContent = priceText;
    if (stockEl) stockEl.textContent = available ? "In stock" : "Sold out";

    const qty = Number(document.getElementById("pdpQtyInput")?.value || 1);
    [addBtn, floatAdd].forEach((btn) => {
      if (!btn) return;
      btn.disabled = !available;
      if (!available) {
        btn.textContent = "Sold Out";
      } else if (btn === addBtn) {
        btn.textContent = "Add to Bag · " + moneyFormat(variant.price * qty);
      } else {
        btn.textContent = "Add to Bag";
      }
    });

    if (addBtn) addBtn.dataset.unitPrice = variant.price;

    showVariantImage(variant.featured_image);
  }

  if (variantWrapper) {
    variantWrapper.querySelectorAll(".pdp-variant-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const optionIndex = Number(btn.dataset.optionIndex);
        const value = btn.dataset.value;

        selectedOptions[optionIndex] = value;

        variantWrapper
          .querySelectorAll('.pdp-variant-btn[data-option-index="' + optionIndex + '"]')
          .forEach((b) => b.classList.toggle("active", b === btn));

        applyVariant(findMatchingVariant());
      });
    });
  }

  /* ---------- quantity ---------- */
  const qtyVal = document.getElementById("pdpQtyVal");
  const qtyInput = document.getElementById("pdpQtyInput");
  const qtyDown = document.getElementById("pdpQtyDown");
  const qtyUp = document.getElementById("pdpQtyUp");

  function currentUnitPrice() {
    return Number(addBtn?.dataset.unitPrice || 0);
  }

  function updateQty(delta) {
    let q = parseInt(qtyVal.textContent, 10) + delta;
    if (q < 1) q = 1;
    if (q > 10) q = 10;
    qtyVal.textContent = q;
    if (qtyInput) qtyInput.value = q;

    if (addBtn && !addBtn.disabled) {
      addBtn.textContent = "Add to Bag · " + moneyFormat(currentUnitPrice() * q);
    }
  }

  if (qtyDown) qtyDown.addEventListener("click", () => updateQty(-1));
  if (qtyUp) qtyUp.addEventListener("click", () => updateQty(1));

  /* ---------- add to bag (AJAX) ----------
     Submits to /cart/add.js instead of a normal browser POST, then dispatches
     the theme's shared 'cart:update' event so the existing global TGC cart
     drawer (assets/cart-drawer-tgc.js) refreshes itself and opens — same
     event contract assets/product-form.js already uses elsewhere in the
     theme. No second drawer, no second AJAX-cart implementation. */
  const pdpForm = document.getElementById("pdpProductForm");
  const addError = document.getElementById("pdpAddError");
  let isSubmitting = false;

  function showAddError(message) {
    if (!addError) return;
    addError.textContent = message;
    addError.hidden = false;
  }

  function hideAddError() {
    if (!addError) return;
    addError.hidden = true;
    addError.textContent = "";
  }

  if (pdpForm) {
    pdpForm.addEventListener("submit", (e) => {
      e.preventDefault();
      if (isSubmitting) return;

      const submitButtons = [addBtn, floatAdd].filter(Boolean);
      if (submitButtons.some((btn) => btn.disabled)) return;

      isSubmitting = true;
      hideAddError();

      const originalLabels = submitButtons.map((btn) => btn.textContent);
      submitButtons.forEach((btn) => {
        btn.disabled = true;
        btn.textContent = "Adding…";
      });

      const formData = new FormData(pdpForm);

      fetch("/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            throw new Error(data.description || data.message || "Could not add this item to your bag.");
          }

          document.dispatchEvent(
            new CustomEvent("cart:update", {
              bubbles: true,
              detail: { data: { source: "pdp-product-form" } },
            })
          );
        })
        .catch((err) => {
          showAddError(err.message || "Something went wrong. Please try again.");
        })
        .finally(() => {
          isSubmitting = false;
          if (productData) {
            applyVariant(findMatchingVariant());
          } else {
            submitButtons.forEach((btn, i) => {
              btn.disabled = !initialVariantAvailable;
              btn.textContent = originalLabels[i];
            });
            if (initialVariantAvailable) updateQty(0);
          }
        });
    });
  }

  /* ---------- floating purchase tray ---------- */
  const floatTray = document.getElementById("pdpFloatTray");

  if (floatTray && addBtn) {
    let trayUpdateQueued = false;
    function updateFloatTray() {
      const headerOffset = window.matchMedia("(max-width: 860px)").matches ? 64 : 88;
      const pastOriginalButton = addBtn.getBoundingClientRect().bottom <= headerOffset;
      const productSection = floatTray.closest(".pdp-custom-section");
      const withinProductSection = !productSection || productSection.getBoundingClientRect().bottom > headerOffset + 80;
      floatTray.classList.toggle("is-visible", pastOriginalButton && withinProductSection);
      trayUpdateQueued = false;
    }
    function queueFloatTrayUpdate() {
      if (trayUpdateQueued) return;
      trayUpdateQueued = true;
      requestAnimationFrame(updateFloatTray);
    }
    window.addEventListener("scroll", queueFloatTrayUpdate, { passive: true });
    window.addEventListener("resize", queueFloatTrayUpdate);
    updateFloatTray();
  }

  /* ---------- cart drawer ----------
     The drawer itself (open/close/quantity/remove) is now the shared global
     component — see assets/cart-drawer-tgc.js, loaded alongside this file.
     That script also keeps #pdpBagCount/#pdpCartHeadCount in sync. */

  /* ---------- share ---------- */
  const shareLink = document.getElementById("pdpShareLink");
  if (shareLink) {
    shareLink.addEventListener("click", (e) => {
      e.preventDefault();
      const url = shareLink.dataset.shareUrl;
      const title = shareLink.dataset.shareTitle;

      if (navigator.share) {
        navigator.share({ title, url }).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(() => {
          const original = shareLink.textContent;
          shareLink.textContent = "Link copied";
          setTimeout(() => {
            shareLink.textContent = original;
          }, 1500);
        });
      }
    });
  }

  /* ---------- accordion ---------- */
  document.querySelectorAll(".pdp-accordion-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const item = btn.closest(".pdp-accordion-item");
      const wasOpen = item.classList.contains("is-open");
      const glyph = btn.querySelector(".pdp-accordion-glyph");

      document.querySelectorAll(".pdp-accordion-item").forEach((el) => {
        el.classList.remove("is-open");
        const g = el.querySelector(".pdp-accordion-glyph");
        if (g) g.textContent = "+";
      });

      if (!wasOpen) {
        item.classList.add("is-open");
        if (glyph) glyph.textContent = "−";
      }
    });
  });

  /* ---------- mobile menu ---------- */
  const menuToggle = document.getElementById("pdpMenuToggle");
  const mobileMenu = document.getElementById("pdpMobileMenu");

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener("click", () => {
      const isOpen = !mobileMenu.hidden;
      mobileMenu.hidden = isOpen;
      menuToggle.setAttribute("aria-expanded", String(!isOpen));
    });
    mobileMenu.addEventListener("click", (e) => {
      if (e.target.tagName === "A") {
        mobileMenu.hidden = true;
        menuToggle.setAttribute("aria-expanded", "false");
      }
    });
  }
})();

